// A session: audio check, tap-along calibration, then the cued round on each
// interface in a counterbalanced order, then two preference items, then the
// participant's own summary line. Opting out is one tap and returns to free
// play with nothing further logged.

import { createCuedRound } from './cued-round.js'
import { createGuardMonitor } from './monitor.js'
import { createMetronome } from './cue.js'
import { conditionOrder } from './counterbalance.js'
import { audioEnvironment, measureClockResolution, measureDisplayPeriod } from '../clock.js'

const ROUTES = ['speaker', 'wired', 'bluetooth', 'muted']

export function createSession({ engine, api, params, logger, mount, host }) {
  const monitor = createGuardMonitor()
  const metronome = createMetronome({ engine })
  let cancelled = false
  let round = null

  // One self-reported column explains more of the asynchrony variance than any
  // platform latency property does, because no API reports Bluetooth reliably.
  function ask(question, options) {
    return new Promise((resolve) => {
      host.replaceChildren()
      const box = document.createElement('div')
      box.className = 'study-ask'
      const h = document.createElement('p')
      h.textContent = question
      box.append(h)
      for (const opt of options) {
        const b = document.createElement('button')
        b.textContent = opt.label ?? opt
        b.onclick = () => resolve(opt.value ?? opt)
        box.append(b)
      }
      const out = document.createElement('button')
      out.className = 'opt-out'
      out.textContent = 'leave the study'
      out.onclick = () => { cancelled = true; resolve(null) }
      box.append(out)
      host.append(box)
    })
  }

  function say(text, sub = '') {
    host.replaceChildren()
    const p = document.createElement('p')
    p.className = 'study-say'
    p.textContent = text
    host.append(p)
    if (sub) {
      const s = document.createElement('p')
      s.className = 'hint'
      s.textContent = sub
      host.append(s)
    }
  }

  return {
    async run() {
      cancelled = false
      await engine.unlock()

      const env = {
        ...audioEnvironment(),
        clock_res_ms: measureClockResolution(),
        display_period_ms: await measureDisplayPeriod(),
        schema_version: __SCHEMA_VERSION__,
        app_version: __APP_VERSION__,
        dpr: devicePixelRatio,
        viewport_w: innerWidth,
        viewport_h: innerHeight,
        screen_w: screen.width,
        screen_h: screen.height,
      }

      // Audio check. iOS has flip-flopped on whether the hardware mute switch
      // silences Web Audio, so ask rather than infer from API state.
      metronome.start()
      const heard = await ask('You should be hearing a click. Do you?', [
        { label: 'yes', value: true }, { label: 'no', value: false },
      ])
      if (cancelled) { metronome.stop(); return null }

      const route = await ask('How are you listening?', ROUTES)
      if (cancelled) { metronome.stop(); return null }

      const common = {
        ...env,
        heard_click: heard,
        audio_route_self: route,
        camera_active: false,
      }

      round = createCuedRound({ engine, api, params, logger, monitor, metronome, env })

      const order = conditionOrder(logger.participantId, mount.studyInterfaceIds())
      const allRows = []

      // Calibration: tap along with the click. Its median gives the session a
      // zero for asynchrony; it does not separate device latency from the
      // human's own negative mean asynchrony, and is not meant to.
      // Calibration is a tap-along task, so it runs on a tapping interface --
      // not on whichever condition happens to come first in this participant's
      // order, which may be the camera or the CLI.
      const calInterface = mount.studyInterfaceIds().includes('pads') ? 'pads' : order[0]
      say('Tap along with the click.', 'A few taps, then we start.')
      await mount.switchTo(calInterface)
      const calRows = await round.run({
        handle: mount.currentHandle(),
        interfaceId: calInterface,
        block: 'calibration',
        common: { ...common, condition_order: order.join('>'), condition_index: 0 },
      })
      allRows.push(...calRows)

      const calValues = calRows.map((r) => r.asynchrony_ms).filter((v) => typeof v === 'number')
      calValues.sort((a, b) => a - b)
      const calibMedian = calValues.length ? calValues[Math.floor(calValues.length / 2)] : ''
      common.calib_median_ms = calibMedian

      for (const [i, interfaceId] of order.entries()) {
        if (cancelled) break
        say(`Next: ${interfaceId}.`, 'Return to the home position, then respond to each cue as fast as you can.')
        await mount.switchTo(interfaceId)
        await new Promise((r) => setTimeout(r, 1200))
        const rows = await round.run({
          handle: mount.currentHandle(),
          interfaceId,
          block: 'main',
          common: {
            ...common,
            condition_order: order.join('>'),
            condition_index: i + 1,
            camera_active: interfaceId === 'camera',
          },
        })
        allRows.push(...rows)
      }

      metronome.stop()
      if (cancelled) return null

      const best = await ask('Which felt best to play?', order)
      const easiest = cancelled ? null : await ask('Which was easiest to hit accurately?', order)

      logger.session({
        ...common,
        condition_order: order.join('>'),
        pref_best: best ?? '',
        pref_easiest: easiest ?? '',
        n_trials: allRows.length,
      })

      const summary = logger.summaryLine()
      say('Done — thank you.', summary)
      return { rows: allRows, summary, order }
    },

    cancel() {
      cancelled = true
      round?.abort()
      metronome.stop()
    },

    dispose() {
      monitor.dispose()
      metronome.stop()
    },
  }
}
